import type {
  MealDay,
  MealDaySummary,
  MealMyDay,
  MealReport,
  MealSubscriptionItem,
  MealsMeta,
} from '@erp/shared';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api } from './api';

/** Обеды — все запросы под ключом ['meals'], realtime meals.changed перечитывает их. */

export const mealsMetaQuery = queryOptions({
  queryKey: ['meals', 'meta'],
  queryFn: () => api<MealsMeta>('/meals/meta'),
  staleTime: 60_000,
});

export const mealsMyQuery = (from: string, days: number) =>
  queryOptions({
    queryKey: ['meals', 'my', from, days],
    queryFn: () => api<MealMyDay[]>('/meals/my', { query: { from, days } }),
  });

export const mealDaysQuery = (from: string, days: number) =>
  queryOptions({
    queryKey: ['meals', 'days', from, days],
    queryFn: () => api<MealDaySummary[]>('/meals/days', { query: { from, days } }),
    placeholderData: keepPreviousData,
  });

export const mealDayQuery = (date: string) =>
  queryOptions({
    queryKey: ['meals', 'day', date],
    queryFn: () => api<MealDay>(`/meals/days/${date}`),
    placeholderData: keepPreviousData,
  });

export const mealSubscriptionsQuery = queryOptions({
  queryKey: ['meals', 'subscriptions'],
  queryFn: () => api<MealSubscriptionItem[]>('/meals/subscriptions'),
});

export const mealReportQuery = (month: string) =>
  queryOptions({
    queryKey: ['meals', 'report', month],
    queryFn: () => api<MealReport>('/meals/report', { query: { month } }),
    placeholderData: keepPreviousData,
  });

const weekdayFmt = new Intl.DateTimeFormat('ru-RU', { weekday: 'short', timeZone: 'UTC' });
const weekdayLong = new Intl.DateTimeFormat('ru-RU', { weekday: 'long', timeZone: 'UTC' });
const dayMonth = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone: 'UTC' });
const atFmt = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const utc = (d: string) => new Date(`${d}T00:00:00Z`);
/** «пн» */
export const weekdayShort = (d: string) => weekdayFmt.format(utc(d));
/** «Вторник, 4 ноября» */
export const dayTitle = (d: string) => {
  const w = weekdayLong.format(utc(d));
  return `${w[0]?.toUpperCase()}${w.slice(1)}, ${dayMonth.format(utc(d))}`;
};
/** «4 ноября» */
export const dayMonthText = (d: string) => dayMonth.format(utc(d));
/** «3 нояб., 20:00» — момент в часовом поясе браузера */
export const atText = (iso: string) => atFmt.format(new Date(iso));

export function portions(n: number) {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'порция';
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return 'порции';
  return 'порций';
}
